// The painted marks of the five point of interest sets (R2714, mockup round
// 114, ui-map-interface.md §17.3.69).
//
// One set draws all five kinds of point of interest (v2-the-deep.md §4.2)
// in their states. The segment and circle batches draw no filled polygon,
// no clip and no gradient, and the sets need all three, so the atlas bakes
// each mark one time as a cell (`poi:<set>:<kind>:<state>:<m>`), and the
// quad carries the place, the turn and the fade. The words of a set, the
// reach of a current gate and of a lair, the figure of a timer and every
// part that moves, are the piece's own work
// (`fast/chart/pieces/points-of-interest.ts`).
//
// This is a leaf (`leaves.test.ts`): it paints into a context it is handed.
// The geometry is the card's own, one card px one world px at 1x, as the
// page `archive/mockups/114-poi-marks.html` draws it; an alpha here is the
// card's ink weight, and the names say which part of the card it paints.
//
// **A CELL TURNS WITH THE FLOW.** The mark of a current gate is painted
// pointing east, the way the tide runs at flood; the quad turns it to the
// current of its cell, and half a turn more at ebb.

import { HEX_SIZE, POI_STYLES } from '../gen/balance';
import { rgba } from './color';
import { hexPath } from './marks';
import { TAU } from './math';

/** One set of the Marks field `poi`. */
export type PoiSet = (typeof POI_STYLES)[number];

/** The five kinds, in the order of `PoiKind` on the wire (1 to 5). */
export const POI_KIND_SLUGS = ['vent', 'cache', 'lair', 'gate', 'ruin'] as const;
export type PoiKindSlug = (typeof POI_KIND_SLUGS)[number];

/**
 * The state of a point, as round 114 names it. `ready`: the vent is full,
 * the cache not dug, the monster at home, the gate at flood, the Data Core
 * waits. `worked`: the vent holds less than its full stock, an own hull
 * works a SEARCH at the cache or the ruin, the monster is out on the roam,
 * the gate is at slack. `spent`: the vent is worked out, the cache is dug,
 * the monster is gone, the reader took the Data Core of the ruin, the gate
 * is at ebb. The vent, the lair and a cache dug by another commander read
 * the live rows of the wire (R2735).
 */
export const POI_STATES = ['ready', 'worked', 'spent'] as const;
export type PoiState = (typeof POI_STATES)[number];

/** The inks of round 114, as packed `0xrrggbb` numbers. The caller passes
 *  them in, because the palette owns them and a leaf holds no colour. */
export interface PoiInks {
  /** The pale chart ink: the text weight. */
  pale: number;
  /** The deep water black under a mark. */
  deep: number;
  /** The own cyan: own work alone. */
  accent: number;
  /** The isotope hue of a vent. */
  iso: number;
  /** The credits hue of a seeded cache. */
  credit: number;
  /** The NPC ink of a lair. */
  npc: number;
  /** The neutral ink of a current gate. */
  neutral: number;
}

/** The hue of each kind: what the point gives. */
export function poiHue(kind: PoiKindSlug, inks: PoiInks): number {
  switch (kind) {
    case 'vent': return inks.iso;
    case 'cache': return inks.credit;
    case 'lair': return inks.npc;
    case 'gate': return inks.neutral;
    default: return inks.pale;
  }
}

/** The figures of the piece a bake reads. */
export interface PoiBakeFig {
  slack_below: number;
  vent_steps: number;
  mag_step: number;
  abbr_arrow_px: number;
  abbr_arrow_per_tide: number;
}

// ---------------------------------------------------------------------------
// The atlas keys
// ---------------------------------------------------------------------------

/** The key of one baked mark. `m` is the tide factor of a current gate in
 *  steps of `mag_step`, the stock share of a vent at work in steps of
 *  `vent_steps`, and 0 for every other kind and state. */
export function poiCellKey(set: PoiSet, kind: PoiKindSlug, state: PoiState, m: number): string {
  return `poi:${set}:${kind}:${state}:${m}`;
}

/** The key of the filled hex the REACH set lays on the water of a gate. */
export const POI_HEX_KEY = 'poi:hex';

/** The parts of a key, or null when it is not a mark of a set. */
export function readPoiCellKey(key: string):
{ set: PoiSet; kind: PoiKindSlug; state: PoiState; m: number } | null {
  const p = key.split(':');
  if (p.length !== 5 || p[0] !== 'poi') return null;
  const set = p[1] as PoiSet, kind = p[2] as PoiKindSlug, state = p[3] as PoiState;
  const m = Number(p[4]);
  if (!(POI_STYLES as readonly string[]).includes(set)) return null;
  if (!POI_KIND_SLUGS.includes(kind) || !POI_STATES.includes(state)) return null;
  if (!Number.isInteger(m) || m < 0) return null;
  return { set, kind, state, m };
}

/** The tide factor a gate cell is baked at: + at flood, - at ebb, its size
 *  `m` steps, and 0 at slack, so the state and never the rounding decides
 *  the slack look. */
export function bakedTide(state: PoiState, m: number, fig: PoiBakeFig): number {
  if (state === 'worked') return 0;
  const mag = m * fig.mag_step;
  return state === 'spent' ? -mag : mag;
}

/** The state of a current gate under a tide factor: flood, slack or ebb. */
export function gateState(tide: number, fig: PoiBakeFig): PoiState {
  if (Math.abs(tide) < fig.slack_below) return 'worked';
  return tide > 0 ? 'ready' : 'spent';
}

/** The size step of a tide factor, as the key carries it. */
export function tideSteps(tide: number, fig: PoiBakeFig): number {
  return fig.mag_step > 0 ? Math.round(Math.abs(tide) / fig.mag_step) : 0;
}

/** The stock share a vent cell is baked at: full when ready, empty when
 *  spent, and `m` steps of `vent_steps` at work (R2735). */
export function bakedShare(state: PoiState, m: number, fig: PoiBakeFig): number {
  if (state === 'ready') return 1;
  if (state === 'spent' || fig.vent_steps <= 0) return 0;
  return Math.min(1, m / fig.vent_steps);
}

/** The step of a stock share, as the key of a vent at work carries it. A
 *  share above 0 bakes at least one step, so a low vent keeps a trace. */
export function shareSteps(share: number, fig: PoiBakeFig): number {
  if (fig.vent_steps <= 0) return 0;
  return Math.max(1, Math.min(fig.vent_steps, Math.round(share * fig.vent_steps)));
}

/** The arrow length of a CHART ABBREVIATION gate at a tide factor. */
export function abbrArrowPx(mag: number, fig: PoiBakeFig): number {
  return fig.abbr_arrow_px + fig.abbr_arrow_per_tide * mag;
}

// ---------------------------------------------------------------------------
// The pen
// ---------------------------------------------------------------------------

type C = CanvasRenderingContext2D;
const PI = Math.PI;

function disc(c: C, x: number, y: number, r: number, ink: number, a: number): void {
  c.beginPath(); c.arc(x, y, r, 0, TAU); c.fillStyle = rgba(ink, a); c.fill();
}

function ring(c: C, x: number, y: number, r: number, ink: number, a: number, w: number, dash?: number[]): void {
  c.beginPath(); c.arc(x, y, r, 0, TAU); c.lineWidth = w; c.strokeStyle = rgba(ink, a);
  c.setLineDash(dash ?? []); c.stroke(); c.setLineDash([]);
}

function arcAt(c: C, r: number, a0: number, a1: number, ink: number, a: number, w: number): void {
  c.beginPath(); c.arc(0, 0, r, a0, a1); c.lineWidth = w; c.strokeStyle = rgba(ink, a);
  c.lineCap = 'round'; c.stroke();
}

function line(c: C, x0: number, y0: number, x1: number, y1: number, ink: number, a: number, w: number): void {
  c.beginPath(); c.moveTo(x0, y0); c.lineTo(x1, y1); c.lineWidth = w;
  c.strokeStyle = rgba(ink, a); c.lineCap = 'round'; c.stroke();
}

function poly(c: C, pts: readonly (readonly [number, number])[], close = true): void {
  c.beginPath();
  pts.forEach((p, i) => (i ? c.lineTo(p[0], p[1]) : c.moveTo(p[0], p[1])));
  if (close) c.closePath();
}

function stroke(c: C, ink: number, a: number, w: number, dash?: number[]): void {
  c.lineWidth = w; c.strokeStyle = rgba(ink, a); c.lineJoin = 'round'; c.lineCap = 'round';
  c.setLineDash(dash ?? []); c.stroke(); c.setLineDash([]);
}

function fill(c: C, ink: number, a: number): void {
  c.fillStyle = rgba(ink, a); c.fill();
}

/** One map cell, pointy side up, as the map grid lays it, as a new path. */
function cellPath(c: C, x: number, y: number, r: number): void {
  c.beginPath(); hexPath(c, x, y, r);
}

/** The glyphs that REACH draws in its disc: each builds a path round the
 *  origin, about 16 px across, and the caller strokes it. */
function glyphPath(c: C, kind: PoiKindSlug, tide: number, fig: PoiBakeFig): void {
  switch (kind) {
    case 'vent':
      c.beginPath(); c.moveTo(-7, 7); c.lineTo(-2, -1); c.lineTo(2, -1); c.lineTo(7, 7);
      for (const i of [-1, 0, 1]) { c.moveTo(i * 3.6, -3); c.quadraticCurveTo(i * 3.6 + 3, -6.5, i * 3.6, -10); }
      break;
    case 'cache':
      c.beginPath(); c.moveTo(-5.5, -5.5); c.lineTo(5.5, 5.5); c.moveTo(5.5, -5.5); c.lineTo(-5.5, 5.5);
      break;
    case 'lair':
      // The eye of the monster: an almond and a slit pupil.
      c.beginPath(); c.moveTo(-8.5, 0); c.quadraticCurveTo(0, -8.5, 8.5, 0); c.quadraticCurveTo(0, 8.5, -8.5, 0);
      c.moveTo(0, -3.8); c.lineTo(0, 3.8);
      break;
    case 'gate':
      // Two posts and the arrow of the flow; at slack two bars.
      c.beginPath(); c.moveTo(-8, -6.5); c.lineTo(-8, 6.5); c.moveTo(8, -6.5); c.lineTo(8, 6.5);
      if (Math.abs(tide) < fig.slack_below) {
        c.moveTo(-3.5, -1.6); c.lineTo(3.5, -1.6); c.moveTo(-3.5, 1.6); c.lineTo(3.5, 1.6);
      } else {
        c.moveTo(-4.5, 0); c.lineTo(4.5, 0); c.moveTo(1.2, -3.2); c.lineTo(4.5, 0); c.lineTo(1.2, 3.2);
      }
      break;
    default:
      // A broken arch: a base, two columns, a broken third, a broken lintel.
      c.beginPath(); c.moveTo(-8, 7); c.lineTo(8, 7);
      c.moveTo(-5.5, 7); c.lineTo(-5.5, -4); c.moveTo(0.5, 7); c.lineTo(0.5, -4); c.moveTo(6, 7); c.lineTo(6, 2);
      c.moveTo(-8, -4); c.lineTo(3, -4); c.lineTo(5, -6.5);
      break;
  }
}

/** The tick of a taken ruin, right of the mark. */
function tick(c: C, x: number, ink: number, a: number): void {
  poly(c, [[x, 0], [x + 3, 3.5], [x + 8, -3.5]], false); stroke(c, ink, a, 1.3);
}

// ---------------------------------------------------------------------------
// The five sets
// ---------------------------------------------------------------------------

/** Card 01, CHART ABBREVIATION: a thin symbol in the chart ink. The name
 *  and the note are words of the piece. */
function paintAbbreviation(c: C, kind: PoiKindSlug, state: PoiState, tide: number, inks: PoiInks, fig: PoiBakeFig): void {
  const ink = inks.pale, spent = state === 'spent' && kind !== 'gate';
  const a = spent ? 0.5 : 0.9, w = 1.1;
  switch (kind) {
    case 'vent':
      disc(c, 0, 0, 2.2, ink, a);
      for (const r of [-0.65, 0, 0.65]) {
        const g = -PI / 2 + r;
        line(c, Math.cos(g) * 4.5, Math.sin(g) * 4.5, Math.cos(g) * 10, Math.sin(g) * 10, ink, a, w);
      }
      break;
    case 'cache':
      ring(c, 0, 0, 8, ink, a, w, [1, 2.4]);
      if (!spent) { line(c, -3.5, -3.5, 3.5, 3.5, ink, a, w); line(c, 3.5, -3.5, -3.5, 3.5, ink, a, w); }
      break;
    case 'lair':
      ring(c, 0, 0, 10, ink, a, w, [1, 2.6]);
      c.beginPath();
      c.moveTo(-4, 0); c.lineTo(4, 0); c.moveTo(0, -4); c.lineTo(0, 4);
      c.moveTo(-2.8, -2.8); c.lineTo(2.8, 2.8); c.moveTo(2.8, -2.8); c.lineTo(-2.8, 2.8);
      stroke(c, ink, a, w);
      break;
    case 'gate': {
      const mag = Math.abs(tide);
      if (mag < fig.slack_below) { ring(c, 0, 0, 3.5, ink, a, w); break; }
      const L = abbrArrowPx(mag, fig);
      line(c, -L / 2, 0, L / 2, 0, ink, a, w);
      poly(c, [[L / 2 - 5, -3], [L / 2, 0], [L / 2 - 5, 3]], false); stroke(c, ink, a, w);
      // The feathers of the flood stream.
      if (tide > 0) {
        line(c, -L / 2, 0, -L / 2 - 3, -3.5, ink, a, w);
        line(c, -L / 2 + 3.5, 0, -L / 2 + 0.5, -3.5, ink, a, w);
      }
      break;
    }
    default:
      c.beginPath(); c.rect(-11, -8, 22, 16); stroke(c, ink, a * 0.8, w, [1, 2.4]);
      if (spent) tick(c, 13, ink, 0.75);
      break;
  }
}

/** Card 04, SEA BED: the point changes the sea bed. The specks of a vent,
 *  the flash of a cache and the stream of a gate move, so the piece draws
 *  them; a gate bakes nothing. */
function paintSeaBed(c: C, kind: PoiKindSlug, state: PoiState, share: number, inks: PoiInks): void {
  const spent = state === 'spent';
  switch (kind) {
    case 'vent': {
      // The pool dims as the stock falls; a worked-out vent is a cold ring.
      if (!spent) {
        const g = c.createRadialGradient(0, 0, 0, 0, 0, 24);
        g.addColorStop(0, rgba(inks.iso, 0.36 * 0.9 * (0.35 + 0.65 * share))); g.addColorStop(1, rgba(inks.iso, 0));
        c.fillStyle = g; c.beginPath(); c.arc(0, 0, 24, 0, TAU); c.fill();
      } else {
        ring(c, 0, 0, 9, inks.pale, 0.3, 1, [2, 3]);
      }
      for (const p of [[-4, 2], [0, -1], [4, 2.5]]) disc(c, p[0], p[1], 1.5, inks.pale, spent ? 0.35 : 0.85);
      break;
    }
    case 'cache':
      [5, 9, 13].forEach((r, i) => {
        c.beginPath();
        for (let j = 0; j <= 30; j++) {
          const a = (j / 30) * TAU, q = r * (1 + 0.12 * Math.sin(3 * a + i));
          if (j) c.lineTo(Math.cos(a) * q, Math.sin(a) * q * 0.8); else c.moveTo(Math.cos(a) * q, Math.sin(a) * q * 0.8);
        }
        stroke(c, inks.pale, (spent ? 0.18 : 0.42) - i * 0.1, 1, spent ? [2, 3] : undefined);
      });
      if (spent) {
        c.beginPath(); c.ellipse(0, 0, 4.5, 3.6, 0, 0, TAU); fill(c, inks.deep, 0.9); stroke(c, inks.pale, 0.4, 1);
      } else {
        disc(c, 1, -1, 1.4, inks.credit, 0.55);
      }
      if (state === 'worked') arcAt(c, 17, -PI / 2, -PI / 2 + TAU, inks.accent, 0.8, 1.6);
      break;
    case 'lair':
      c.beginPath();
      for (let j = 0; j <= 24; j++) {
        const a = (j / 24) * TAU, q = 15 * (1 + 0.1 * Math.sin(4 * a + 1) + 0.06 * Math.sin(9 * a));
        if (j) c.lineTo(Math.cos(a) * q, Math.sin(a) * q * 0.75); else c.moveTo(Math.cos(a) * q, Math.sin(a) * q * 0.75);
      }
      fill(c, inks.deep, 0.8);
      for (let j = 0; j < 12; j++) {
        const a = (j / 12) * TAU + 0.2;
        disc(c, Math.cos(a) * 17, Math.sin(a) * 13, 1.2 + (j % 3) * 0.4, inks.pale, spent ? 0.18 : 0.45);
      }
      // The two eyes of the monster at home; the pit is empty while it is
      // out or gone.
      if (state === 'ready') { disc(c, -3.2, 0, 1.5, inks.npc, 0.95); disc(c, 3.2, 0, 1.5, inks.npc, 0.95); }
      break;
    case 'gate':
      break;
    default:
      c.beginPath();
      c.moveTo(-12, -8); c.lineTo(4, -8); c.moveTo(8, -8); c.lineTo(12, -8); c.lineTo(12, 8); c.lineTo(-12, 8); c.lineTo(-12, -8);
      c.moveTo(-2, -8); c.lineTo(-2, 2); c.moveTo(-12, 2); c.lineTo(-6, 2); c.moveTo(-2, 2); c.lineTo(5, 2);
      c.moveTo(5, 2); c.lineTo(5, 8);
      stroke(c, inks.pale, spent ? 0.28 : 0.62, 1.2);
      if (state === 'worked') { c.beginPath(); c.rect(-12, -8, 24, 16); fill(c, inks.accent, 0.12); }
      if (spent) tick(c, 15, inks.pale, 0.7);
      break;
  }
}

/** Card 06, DOT AND LABEL: the dot on the cell. The label is the piece's. */
function paintDot(c: C, inks: PoiInks): void {
  disc(c, 0, 0, 4, inks.deep, 0.7);
  disc(c, 0, 0, 2.6, inks.pale, 0.95);
}

/** Card 08, REACH: a glyph in a dark disc, and for a vent, a cache and a
 *  ruin the one cell it works on, at true size. The home of a lair and the
 *  water of a gate are the piece's, because they reach past a cell. */
function paintReach(
  c: C, kind: PoiKindSlug, state: PoiState, tide: number, share: number, inks: PoiInks, fig: PoiBakeFig,
): void {
  const ink = poiHue(kind, inks), spent = state === 'spent' && kind !== 'gate';
  const R = HEX_SIZE;
  if (kind === 'vent' || kind === 'cache' || kind === 'ruin') {
    c.save(); cellPath(c, 0, 0, R); c.clip();
    if (!spent) {
      if (kind === 'vent') {
        c.beginPath();
        for (let y = -24; y <= 24; y += 4) {
          for (let x = -24 + ((y / 4) % 2 ? 2 : 0); x <= 24; x += 4) c.rect(x - 0.6, y - 0.6, 1.2, 1.2);
        }
        // The stipple thins with the stock of the vent.
        fill(c, ink, 0.12 + 0.4 * share);
      }
      if (kind === 'cache') {
        c.beginPath();
        for (let d = -48; d <= 48; d += 5) { c.moveTo(d, -24); c.lineTo(d + 48, 24); }
        stroke(c, ink, 0.22, 1);
      }
      if (kind === 'ruin') {
        c.beginPath();
        for (let y = -24, r = 0; y <= 24; y += 6, r++) {
          c.moveTo(-24, y); c.lineTo(24, y);
          for (let x = -24 + (r % 2) * 5; x <= 24; x += 10) { c.moveTo(x, y); c.lineTo(x, y + 6); }
        }
        stroke(c, ink, 0.18, 0.9);
      }
      if (state === 'worked' && kind !== 'vent') { c.beginPath(); c.rect(-R, -R, 2 * R, 2 * R); fill(c, inks.accent, 0.2); }
    }
    c.restore();
    cellPath(c, 0, 0, R); stroke(c, ink, spent ? 0.3 : 0.7, 1.2, spent ? [2, 3] : undefined);
  }
  disc(c, 0, 0, 8.5, inks.deep, 0.75);
  c.save(); c.scale(0.62, 0.62); glyphPath(c, kind, tide, fig); stroke(c, ink, spent ? 0.5 : 0.95, 2); c.restore();
}

/** Card 10, SHAPE GRAMMAR: the outer shape names what the point does, the
 *  inner glyph the kind. The orbit dot of a SEARCH is the piece's. */
function paintGrammar(
  c: C, kind: PoiKindSlug, state: PoiState, tide: number, share: number, inks: PoiInks, fig: PoiBakeFig,
): void {
  const ink = poiHue(kind, inks), spent = state === 'spent' && kind !== 'gate';
  // A lair whose monster is out is hollow with a dashed edge, as spent is
  // hollow with a whole one.
  const out = kind === 'lair' && state === 'worked', hollow = spent || out;
  const body = (): void => {
    if (hollow) { fill(c, inks.deep, 0.85); stroke(c, ink, 0.7, 1.4, out ? [3, 2.5] : undefined); }
    else { fill(c, ink, 0.95); c.lineWidth = 1; c.strokeStyle = rgba(inks.deep, 0.9); c.stroke(); }
  };
  const gin = hollow ? ink : inks.deep, ga = hollow ? 0.6 : 0.95;
  switch (kind) {
    case 'cache':
    case 'ruin':
      // A diamond: a place to take from.
      poly(c, [[0, -11], [11, 0], [0, 11], [-11, 0]]); body();
      if (kind === 'cache') { line(c, -3.5, -3.5, 3.5, 3.5, gin, ga, 1.8); line(c, 3.5, -3.5, -3.5, 3.5, gin, ga, 1.8); }
      else {
        c.beginPath(); c.rect(-4, -4, 8, 8); stroke(c, gin, ga, 1.3);
        c.beginPath(); c.rect(-1.6, -1.6, 3.2, 3.2); fill(c, gin, ga);
      }
      break;
    case 'vent':
      // A hexagon: a place to gather, with the isotope trefoil.
      c.beginPath();
      for (let i = 0; i < 6; i++) {
        const a = (i * PI) / 3;
        if (i) c.lineTo(Math.cos(a) * 10.5, Math.sin(a) * 10.5); else c.moveTo(Math.cos(a) * 10.5, Math.sin(a) * 10.5);
      }
      c.closePath(); body();
      for (let i = 0; i < 3; i++) {
        const a = -PI / 2 + (i * TAU) / 3;
        c.beginPath(); c.moveTo(Math.cos(a - 0.45) * 1.8, Math.sin(a - 0.45) * 1.8);
        c.arc(0, 0, 6, a - 0.45, a + 0.45); c.closePath(); fill(c, gin, ga);
      }
      disc(c, 0, 0, 1.2, gin, ga);
      // The stock ring: the share of the stock the vent holds (R2735).
      if (!spent && share > 0) arcAt(c, 14.5, -PI / 2, -PI / 2 + TAU * share, ink, 0.8, 2);
      break;
    case 'lair':
      // A point-down triangle: a danger.
      poly(c, [[-11.5, -7], [11.5, -7], [0, 12]]); body();
      line(c, 0, -3.5, 0, 2.5, gin, ga, 1.8); disc(c, 0, 5.6, 1.2, gin, ga);
      break;
    default:
      // A chevron pair: a flow. Two at flood, one at ebb, two bars at slack.
      if (Math.abs(tide) < fig.slack_below) {
        line(c, -6, -2.5, 6, -2.5, ink, 0.85, 2.4); line(c, -6, 2.5, 6, 2.5, ink, 0.85, 2.4);
        break;
      }
      for (let n = tide > 0 ? 2 : 1, i = 0; i < n; i++) {
        const x = (i - (n - 1) / 2) * 7 + 2;
        poly(c, [[x - 4, -8], [x + 3.5, 0], [x - 4, 8]], false);
        c.lineWidth = 4.6; c.strokeStyle = rgba(inks.deep, 0.85); c.lineJoin = 'round'; c.lineCap = 'round'; c.stroke();
        stroke(c, ink, 0.95, 2.6);
      }
      break;
  }
  if (spent && kind === 'ruin') tick(c, 14, ink, 0.75);
}

/** Paints one mark of one set at the origin. The context is scaled and its
 *  path is open; the caller restores nothing. */
export function paintPoiMark(
  c: C, set: PoiSet, kind: PoiKindSlug, state: PoiState, m: number, inks: PoiInks, fig: PoiBakeFig,
): void {
  const tide = kind === 'gate' ? bakedTide(state, m, fig) : 1;
  const share = kind === 'vent' ? bakedShare(state, m, fig) : 1;
  switch (set) {
    case 'chart-abbreviation': paintAbbreviation(c, kind, state, tide, inks, fig); break;
    case 'sea-bed': paintSeaBed(c, kind, state, share, inks); break;
    case 'dot-and-label': paintDot(c, inks); break;
    case 'reach': paintReach(c, kind, state, tide, share, inks, fig); break;
    default: paintGrammar(c, kind, state, tide, share, inks, fig); break;
  }
}

/** The filled hex of the REACH water of a gate, white, so the quad tints it. */
export function paintPoiHex(c: C): void {
  cellPath(c, 0, 0, HEX_SIZE);
  c.fillStyle = '#ffffff'; // mask
  c.fill();
}

/** True where a set bakes nothing for a kind: the stream of a SEA BED gate
 *  is all motion, so the piece draws it. */
export function poiBakesNothing(set: PoiSet, kind: PoiKindSlug): boolean {
  return set === 'sea-bed' && kind === 'gate';
}
